import React from "react";
import { Box, Typography } from "@mui/material";
import { PLACEMENT_BUCKET_COLORS } from "../../types/placement";
import { getBrandLogoUrl } from "../../utils/brandLogoUtils";
import { getS3Url } from "../../utils/imageUtils";
import {
  AssetKicker,
  AssetTitle,
  Card,
  CardLabel,
  ChannelAvatar,
  DataTable,
  LinkedInAssetCanvas,
  MethodologyFooter,
  MUTED,
  NAVY,
  SANS,
  CARD_BORDER,
} from "./linkedinAssetChrome";

export type LivestreamPresenceBucket = "content" | "physical" | "banner" | "credits";

export type LinkedInLivestreamBrandsData = {
  stream: {
    id: string;
    title: string;
    programName: string | null;
    channelName: string;
    channelImageS3Key: string | null;
    channelImageUrl: string | null;
    channelColor: string | null;
    startedAt: string | null;
    avgViewers?: number;
    peakViewers?: number;
    chatMessages?: number | null;
    minutes: Array<{ minute: number; viewers: number; chat?: number }>;
  };
  events: Array<{
    id: string;
    brandName: string;
    brandId?: string | null;
    bucket: LivestreamPresenceBucket;
    minute: number;
    mentionType?: "paid" | "organic" | "neutral" | null;
    s3Key?: string | null;
    logoUrl?: string | null;
  }>;
  brands: Array<{
    brandId: string | null;
    brandName: string;
    firstMinute: number;
    counts: Record<LivestreamPresenceBucket, number>;
    total: number;
    s3Key?: string | null;
    logoUrl?: string | null;
  }>;
};

const PRESENCE_TYPES: Array<{
  key: LivestreamPresenceBucket;
  label: string;
}> = [
  { key: "content", label: "Mención" },
  { key: "physical", label: "Placement" },
  { key: "banner", label: "Banner" },
  { key: "credits", label: "Créditos" },
];

const PRESENCE_LABEL: Record<LivestreamPresenceBucket, string> = {
  content: "Mención",
  physical: "Placement",
  banner: "Banner",
  credits: "Créditos",
};

/** Brands shown in the consolidada table; the rest become "+N marcas más". */
export const LIVESTREAM_BRANDS_MAX_TABLE_ROWS = 3;

function channelImageSrc(stream: LinkedInLivestreamBrandsData["stream"]): string {
  if (stream.channelImageS3Key) return getS3Url(stream.channelImageS3Key);
  if (stream.channelImageUrl) return stream.channelImageUrl;
  return "";
}

function hueFromName(name: string): number {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1)
    hash = (hash * 33 + name.charCodeAt(index)) >>> 0;
  return hash % 360;
}

function brandColor(name: string): string {
  return `hsl(${hueFromName(name)} 78% 62%)`;
}

function brandInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const BrandMark: React.FC<{
  name: string;
  s3Key?: string | null;
  logoUrl?: string | null;
  size: number;
}> = ({ name, s3Key, logoUrl, size }) => {
  const [failed, setFailed] = React.useState(false);
  const src = failed ? "" : getBrandLogoUrl({ s3Key: s3Key ?? undefined, logoUrl: logoUrl ?? undefined });
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: src ? "transparent" : brandColor(name),
        color: "#1a1a1a",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
        fontFamily: SANS,
        fontWeight: 800,
        fontSize: Math.max(8, size * 0.32),
        lineHeight: 1,
      }}
      title={name}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setFailed(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        brandInitials(name)
      )}
    </Box>
  );
};

function formatHhMm(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = (parts.find((part) => part.type === "hour")?.value ?? "00").padStart(2, "0");
  const minute = (parts.find((part) => part.type === "minute")?.value ?? "00").padStart(2, "0");
  return `${hour}:${minute}`;
}

function formatBroadcastDay(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    weekday: "long",
    day: "numeric",
    month: "short",
  }).formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = (parts.find((part) => part.type === "month")?.value ?? "").replace(".", "");
  if (!weekday || !day || !month) return "";
  return [`${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}`, day, month].join(" ");
}

function formatCount(value: number): string {
  return Math.round(value).toLocaleString("es-CL");
}

function displayBrandName(name: string): string {
  return name.replace(/\bTia Rosa\b/g, "Tía Rosa");
}

function formatChatRate(rate: number | null): string {
  if (rate == null) return "—";
  return formatCount(rate);
}

function episodeConsolidado(
  stream: LinkedInLivestreamBrandsData["stream"],
): { avgViewers: number | null; chat: number } {
  const viewers = stream.minutes.map((point) => point.viewers).filter((value) => value > 0);
  const chatFromMinutes = stream.minutes.reduce((sum, point) => sum + (point.chat ?? 0), 0);
  const avgFromMinutes = viewers.length
    ? Math.round(viewers.reduce((sum, value) => sum + value, 0) / viewers.length)
    : null;
  const avgViewers =
    avgFromMinutes ??
    (stream.avgViewers && stream.avgViewers > 0 ? Math.round(stream.avgViewers) : null);
  const publishedChat = stream.chatMessages && stream.chatMessages > 0 ? Math.round(stream.chatMessages) : 0;
  const chat = publishedChat > 0 ? publishedChat : chatFromMinutes;
  return { avgViewers, chat };
}

function appearanceAudience(
  eventMinutes: number[],
  minutes: MinutePoint[],
): { avgViewers: number | null; chatPerMinute: number | null } {
  const unique: number[] = [];
  for (const minute of eventMinutes) {
    if (!unique.includes(minute)) unique.push(minute);
  }
  if (!unique.length) return { avgViewers: null, chatPerMinute: null };
  const viewers = unique.map((minute) => viewersAtMinute(minutes, minute)).filter((value) => value > 0);
  const rates = unique.map((minute) => chatPerMinuteOnLine(minutes, minute));
  return {
    avgViewers: viewers.length
      ? Math.round(viewers.reduce((sum, value) => sum + value, 0) / viewers.length)
      : null,
    chatPerMinute: rates.reduce((sum, value) => sum + value, 0) / rates.length,
  };
}

function brandTypeKey(brand: LinkedInLivestreamBrandsData["brands"][number]): string {
  return brand.brandId || brand.brandName;
}

function eventBrandKey(event: LinkedInLivestreamBrandsData["events"][number]): string {
  return event.brandId || event.brandName;
}

function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const fraction = value / exp;
  const ceil =
    fraction <= 1 ? 1 : fraction <= 1.5 ? 1.5 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return ceil * exp;
}

type MinutePoint = { minute: number; viewers: number; chat?: number };

function viewersAtMinute(minutes: MinutePoint[], minute: number): number {
  if (!minutes.length) return 0;
  let best = minutes[0]!;
  let bestDist = Math.abs(best.minute - minute);
  for (const point of minutes) {
    const dist = Math.abs(point.minute - minute);
    if (dist < bestDist) {
      best = point;
      bestDist = dist;
    }
  }
  return best.viewers;
}

function curveSeries(points: MinutePoint[], publishedPeak = 0): MinutePoint[] {
  return contiguousSegments(points).flatMap((raw) =>
    keepVisiblePeak(raw, smoothedPoints(raw), publishedPeak),
  );
}

function viewersOnCurve(series: MinutePoint[], minute: number): number {
  if (!series.length) return 0;
  const first = series[0]!;
  const last = series[series.length - 1]!;
  if (minute <= first.minute) return first.viewers;
  if (minute >= last.minute) return last.viewers;
  for (let index = 0; index < series.length - 1; index += 1) {
    const start = series[index]!;
    const end = series[index + 1]!;
    if (minute < start.minute || minute > end.minute) continue;
    if (end.minute === start.minute) return start.viewers;
    const t = (minute - start.minute) / (end.minute - start.minute);
    return start.viewers + (end.viewers - start.viewers) * t;
  }
  return last.viewers;
}

function contiguousSegments(points: MinutePoint[]): MinutePoint[][] {
  const segments: MinutePoint[][] = [];
  let current: MinutePoint[] = [];
  let previousMinute = -1;
  for (const point of points) {
    if (previousMinute >= 0 && point.minute - previousMinute > 2 && current.length) {
      segments.push(current);
      current = [];
    }
    current.push(point);
    previousMinute = point.minute;
  }
  if (current.length) segments.push(current);
  return segments;
}

function smoothedPoints(points: MinutePoint[]): MinutePoint[] {
  const radius = 8;
  const averaged = points.map((point, index) => {
    let weightSum = 0;
    let viewerSum = 0;
    let chatSum = 0;
    for (let offset = -radius; offset <= radius; offset += 1) {
      const neighbor = points[index + offset];
      if (!neighbor) continue;
      const weight = radius + 1 - Math.abs(offset);
      viewerSum += neighbor.viewers * weight;
      chatSum += (neighbor.chat ?? 0) * weight;
      weightSum += weight;
    }
    return {
      minute: point.minute,
      viewers: weightSum > 0 ? viewerSum / weightSum : point.viewers,
      chat: weightSum > 0 ? chatSum / weightSum : point.chat ?? 0,
    };
  });
  const step = 5;
  return averaged.filter(
    (point, index) => index === 0 || index === averaged.length - 1 || point.minute % step === 0,
  );
}

function sampleOnSeries(
  series: MinutePoint[],
  minute: number,
  valueOf: (point: MinutePoint) => number,
): number {
  if (!series.length) return 0;
  const first = series[0]!;
  const last = series[series.length - 1]!;
  if (minute <= first.minute) return valueOf(first);
  if (minute >= last.minute) return valueOf(last);
  for (let index = 0; index < series.length - 1; index += 1) {
    const start = series[index]!;
    const end = series[index + 1]!;
    if (minute < start.minute || minute > end.minute) continue;
    if (end.minute === start.minute) return valueOf(start);
    const t = (minute - start.minute) / (end.minute - start.minute);
    return valueOf(start) + (valueOf(end) - valueOf(start)) * t;
  }
  return valueOf(last);
}

/** Chat/min on the same smoothed series the chart line is drawn from. */
function chatPerMinuteOnLine(minutes: MinutePoint[], minute: number): number {
  let nearest: MinutePoint[] | null = null;
  let nearestDistance = Infinity;
  for (const raw of contiguousSegments(minutes)) {
    if (!raw.length) continue;
    const first = raw[0]!.minute;
    const last = raw[raw.length - 1]!.minute;
    if (minute >= first && minute <= last) {
      return sampleOnSeries(smoothedPoints(raw), minute, (point) => point.chat ?? 0);
    }
    const distance = minute < first ? first - minute : minute - last;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = raw;
    }
  }
  return nearest
    ? sampleOnSeries(smoothedPoints(nearest), minute, (point) => point.chat ?? 0)
    : 0;
}

function curvePath(segment: Array<{ x: number; y: number }>): string {
  if (segment.length === 0) return "";
  if (segment.length === 1) return `M${segment[0]!.x.toFixed(1)} ${segment[0]!.y.toFixed(1)}`;
  let path = `M${segment[0]!.x.toFixed(1)} ${segment[0]!.y.toFixed(1)}`;
  for (let index = 0; index < segment.length - 1; index += 1) {
    const prev = segment[index - 1] ?? segment[index]!;
    const start = segment[index]!;
    const end = segment[index + 1]!;
    const next = segment[index + 2] ?? end;
    const cp1x = start.x + (end.x - prev.x) / 6;
    const cp1y = start.y + (end.y - prev.y) / 6;
    const cp2x = end.x - (next.x - start.x) / 6;
    const cp2y = end.y - (next.y - start.y) / 6;
    path += ` C${cp1x.toFixed(1)} ${cp1y.toFixed(1)} ${cp2x.toFixed(1)} ${cp2y.toFixed(1)} ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
  }
  return path;
}

function keepVisiblePeak(
  raw: MinutePoint[],
  smoothed: MinutePoint[],
  publishedPeak = 0,
): MinutePoint[] {
  if (!raw.length) return smoothed;
  let peak = raw[0]!;
  for (const point of raw) {
    if (point.viewers > peak.viewers) peak = point;
  }
  const viewers = Math.max(peak.viewers, publishedPeak);
  const next = smoothed.map((point) => ({ ...point }));
  const hit = next.find((point) => point.minute === peak.minute);
  if (hit) hit.viewers = viewers;
  else {
    next.push({ minute: peak.minute, viewers, chat: peak.chat });
    next.sort((a, b) => a.minute - b.minute);
  }
  return next;
}

function mappedSegments(
  points: MinutePoint[],
  xFor: (minute: number) => number,
  yOf: (point: MinutePoint) => number,
  keepPeak = false,
  publishedPeak = 0,
): Array<Array<{ x: number; y: number }>> {
  return contiguousSegments(points)
    .map((raw) => {
      const smoothed = smoothedPoints(raw);
      const series = keepPeak ? keepVisiblePeak(raw, smoothed, publishedPeak) : smoothed;
      return series.map((point) => ({ x: xFor(point.minute), y: yOf(point) }));
    })
    .filter((segment) => segment.length > 0);
}

function smoothPath(
  points: MinutePoint[],
  xFor: (minute: number) => number,
  yFor: (viewers: number) => number,
  publishedPeak = 0,
): string {
  return mappedSegments(points, xFor, (point) => yFor(point.viewers), true, publishedPeak)
    .map(curvePath)
    .join(" ");
}

function smoothAreaPath(
  points: MinutePoint[],
  xFor: (minute: number) => number,
  yOf: (point: MinutePoint) => number,
  baselineY: number,
): string {
  return mappedSegments(points, xFor, yOf)
    .map((segment) => {
      const line = curvePath(segment);
      if (!line) return "";
      const first = segment[0]!;
      const last = segment[segment.length - 1]!;
      return `${line} L${last.x.toFixed(1)} ${baselineY.toFixed(1)} L${first.x.toFixed(1)} ${baselineY.toFixed(1)} Z`;
    })
    .join(" ");
}

const CHAT_FILL = "rgba(255, 255, 255, 0.22)";
const CHAT_STROKE = "rgba(255, 255, 255, 0.55)";
const CREDITS_WINDOW_MINUTES = 10;

function asCreditsWindow(
  events: LinkedInLivestreamBrandsData["events"],
  maxMinute: number,
): LinkedInLivestreamBrandsData["events"] {
  return events.map((event) => {
    if (event.bucket !== "content") return event;
    const inOpening = event.minute < CREDITS_WINDOW_MINUTES;
    const inClosing = event.minute > maxMinute - CREDITS_WINDOW_MINUTES;
    if (!inOpening && !inClosing) return event;
    return { ...event, bucket: "credits" };
  });
}

const MarkFigure: React.FC<{ bucket: LivestreamPresenceBucket }> = ({ bucket }) => {
  const color = PLACEMENT_BUCKET_COLORS[bucket];
  if (bucket === "content") {
    return (
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, display: "block", flexShrink: 0 }} />
    );
  }
  if (bucket === "physical") {
    return (
      <Box component="span" aria-hidden sx={{ width: 5, height: 5, borderRadius: "1px", bgcolor: color, display: "block", flexShrink: 0 }} />
    );
  }
  if (bucket === "banner") {
    return (
      <Box component="span" aria-hidden sx={{ width: 8, height: 4, borderRadius: "1px", bgcolor: color, display: "block", flexShrink: 0 }} />
    );
  }
  return (
    <Box component="svg" aria-hidden viewBox="0 0 7 6" sx={{ width: 7, height: 6, display: "block", flexShrink: 0 }}>
      <path d="M3.5 1.1 L6.15 5.15 H0.85 Z" fill={color} stroke={color} strokeWidth={1.15} strokeLinejoin="round" />
    </Box>
  );
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

type ChartMarker = {
  key: string;
  name: string;
  s3Key?: string | null;
  logoUrl?: string | null;
  bucket: LivestreamPresenceBucket;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
};

/** Lightly fan stem feet along the curve so lines do not pile on the same point. */
function spreadStemFeet(
  markers: ChartMarker[],
  curveY: (x: number) => number,
  bounds: { minX: number; maxX: number },
  minSep = 10,
  maxDrift = 28,
): Map<string, { x: number; y: number }> {
  const feet = markers.map((marker) => ({
    key: marker.key,
    home: marker.anchorX,
    x: marker.anchorX,
  }));

  for (let iter = 0; iter < 40; iter += 1) {
    for (let i = 0; i < feet.length; i += 1) {
      for (let j = i + 1; j < feet.length; j += 1) {
        const a = feet[i]!;
        const b = feet[j]!;
        let dx = b.x - a.x;
        if (Math.abs(dx) < 0.01) dx = a.key < b.key ? -1 : 1;
        const dist = Math.abs(dx);
        if (dist >= minSep) continue;
        const push = (minSep - dist) / 2;
        const dir = Math.sign(dx) || 1;
        a.x -= dir * push;
        b.x += dir * push;
      }
    }
    for (const foot of feet) {
      foot.x += (foot.home - foot.x) * 0.18;
      foot.x = clamp(foot.x, foot.home - maxDrift, foot.home + maxDrift);
      foot.x = clamp(foot.x, bounds.minX, bounds.maxX);
    }
  }

  return new Map(
    feet.map((foot) => [foot.key, { x: foot.x, y: curveY(foot.x) }] as const),
  );
}

/** Largest open X slots between occupied positions (and the plot edges). */
function largestFreeSlots(
  occupiedXs: number[],
  count: number,
  bounds: { minX: number; maxX: number },
  minGap: number,
): number[] {
  if (count <= 0) return [];
  const occupied = [...occupiedXs, bounds.minX, bounds.maxX].sort((a, b) => a - b);
  const gaps: Array<{ x: number; width: number }> = [];
  for (let index = 0; index < occupied.length - 1; index += 1) {
    const left = occupied[index]!;
    const right = occupied[index + 1]!;
    const width = right - left;
    if (width < minGap) continue;
    gaps.push({ x: clamp((left + right) / 2, bounds.minX, bounds.maxX), width });
  }
  if (!gaps.length) {
    return Array.from({ length: count }, (_, index) =>
      clamp(
        bounds.minX + ((index + 1) / (count + 1)) * (bounds.maxX - bounds.minX),
        bounds.minX,
        bounds.maxX,
      ),
    );
  }
  return gaps
    .sort((a, b) => b.width - a.width || a.x - b.x)
    .slice(0, count)
    .sort((a, b) => a.x - b.x)
    .map((gap) => gap.x);
}

/** Logo bubbles near each appearance, with iterative repulsion off the curve. */
function layoutSpreadBrandMarkers(
  events: LinkedInLivestreamBrandsData["events"],
  xFor: (minute: number) => number,
  yAtMinute: (minute: number) => number,
  curveY: (x: number) => number,
  size: number,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
): Array<{
  key: string;
  name: string;
  s3Key?: string | null;
  logoUrl?: string | null;
  bucket: LivestreamPresenceBucket;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
}> {
  const minSep = size + 14;
  const preferOrbit = size + 18;
  const curveClear = size * 0.72 + 6;

  type Draft = {
    key: string;
    name: string;
    s3Key?: string | null;
    logoUrl?: string | null;
    bucket: LivestreamPresenceBucket;
    x: number;
    y: number;
    anchorX: number;
    anchorY: number;
    preferSide: 1 | -1;
    /** Physical placements float into open gaps instead of a timestamp. */
    free: boolean;
  };

  const seedSide = (anchorY: number, prefer: 1 | -1): 1 | -1 => {
    const aboveOk = anchorY - preferOrbit >= bounds.minY;
    const belowOk = anchorY + preferOrbit <= bounds.maxY;
    if (prefer < 0 && aboveOk) return -1;
    if (prefer > 0 && belowOk) return 1;
    if (aboveOk) return -1;
    if (belowOk) return 1;
    return prefer;
  };

  const timed = [...events]
    .filter((event) => event.bucket !== "physical")
    .sort(
      (a, b) =>
        a.minute - b.minute ||
        a.brandName.localeCompare(b.brandName) ||
        a.id.localeCompare(b.id),
    );
  const physical = [...events]
    .filter((event) => event.bucket === "physical")
    .sort((a, b) => a.brandName.localeCompare(b.brandName));

  const draft: Draft[] = timed.map((event, index) => {
    const anchorX = clamp(xFor(event.minute), bounds.minX, bounds.maxX);
    const anchorY = yAtMinute(event.minute);
    const preferSide = seedSide(anchorY, index % 2 === 0 ? -1 : 1);
    return {
      key: `${event.id || eventBrandKey(event)}:${event.bucket}:${event.minute}:${index}`,
      name: event.brandName,
      s3Key: event.s3Key,
      logoUrl: event.logoUrl,
      bucket: event.bucket,
      x: anchorX,
      y: clamp(anchorY + preferSide * preferOrbit, bounds.minY, bounds.maxY),
      anchorX,
      anchorY,
      preferSide,
      free: false,
    };
  });

  const keepOffCurve = (item: Draft) => {
    const cy = curveY(item.x);
    const delta = item.y - cy;
    if (Math.abs(delta) >= curveClear) return;
    const dir = delta === 0 ? item.preferSide : (Math.sign(delta) as 1 | -1);
    item.y = clamp(cy + dir * curveClear, bounds.minY, bounds.maxY);
    if (Math.abs(item.y - cy) < curveClear - 0.5) {
      item.y = clamp(cy - dir * curveClear, bounds.minY, bounds.maxY);
      item.preferSide = (Math.sign(item.y - cy) || item.preferSide) as 1 | -1;
    }
  };

  const clampItem = (item: Draft) => {
    item.x = clamp(item.x, bounds.minX, bounds.maxX);
    item.y = clamp(item.y, bounds.minY, bounds.maxY);
  };

  const runForces = (rounds: number) => {
    for (let iter = 0; iter < rounds; iter += 1) {
      for (let i = 0; i < draft.length; i += 1) {
        for (let j = i + 1; j < draft.length; j += 1) {
          const a = draft[i]!;
          const b = draft[j]!;
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          let dist = Math.hypot(dx, dy);
          if (dist < 0.01) {
            dx = a.key < b.key ? -1 : 1;
            dy = a.preferSide;
            dist = Math.hypot(dx, dy);
          }
          if (dist >= minSep) continue;
          const push = ((minSep - dist) / dist) * 0.55;
          // Free placements yield more so timed mentions keep their timestamp.
          const aMass = a.free ? 0.35 : 1;
          const bMass = b.free ? 0.35 : 1;
          const total = aMass + bMass;
          a.x -= dx * push * (bMass / total);
          a.y -= dy * push * (bMass / total);
          b.x += dx * push * (aMass / total);
          b.y += dy * push * (aMass / total);
        }
      }

      const settle = iter < rounds * 0.35 ? 0.22 : iter < rounds * 0.7 ? 0.12 : 0.06;
      for (const item of draft) {
        const preferY = item.anchorY + item.preferSide * preferOrbit;
        if (item.free) {
          // Stay near the gap slot, but allow X to slide into open space.
          item.x += (item.anchorX - item.x) * settle * 0.35;
          item.y += (preferY - item.y) * settle;
          item.anchorX = item.x;
          item.anchorY = curveY(item.x);
        } else {
          item.x += (item.anchorX - item.x) * settle;
          item.y += (preferY - item.y) * settle;
        }
        keepOffCurve(item);
        clampItem(item);
      }
    }
  };

  // Timed mentions first, then drop placements into the biggest open gaps.
  runForces(90);

  const slots = largestFreeSlots(
    draft.map((item) => item.x),
    physical.length,
    bounds,
    minSep * 1.4,
  );
  physical.forEach((event, index) => {
    const slotX =
      slots[index] ??
      clamp(
        bounds.minX + ((index + 1) / (physical.length + 1)) * (bounds.maxX - bounds.minX),
        bounds.minX,
        bounds.maxX,
      );
    const anchorY = curveY(slotX);
    const preferSide = seedSide(anchorY, index % 2 === 0 ? -1 : 1);
    draft.push({
      key: `${event.id || eventBrandKey(event)}:physical:${index}`,
      name: event.brandName,
      s3Key: event.s3Key,
      logoUrl: event.logoUrl,
      bucket: event.bucket,
      x: slotX,
      y: clamp(anchorY + preferSide * preferOrbit, bounds.minY, bounds.maxY),
      anchorX: slotX,
      anchorY,
      preferSide,
      free: true,
    });
  });

  runForces(80);

  // Final hard separation for anything still overlapping.
  for (let pass = 0; pass < 40; pass += 1) {
    let moved = false;
    for (let i = 0; i < draft.length; i += 1) {
      for (let j = i + 1; j < draft.length; j += 1) {
        const a = draft[i]!;
        const b = draft[j]!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let dist = Math.hypot(dx, dy);
        if (dist < 0.01) {
          dx = 1;
          dy = a.preferSide;
          dist = Math.hypot(dx, dy);
        }
        if (dist >= minSep - 0.5) continue;
        moved = true;
        const push = (minSep - dist) / 2;
        const ux = dx / dist;
        const uy = dy / dist;
        const aMass = a.free ? 0.35 : 1;
        const bMass = b.free ? 0.35 : 1;
        const total = aMass + bMass;
        a.x -= ux * push * (bMass / total);
        a.y -= uy * push * (bMass / total);
        b.x += ux * push * (aMass / total);
        b.y += uy * push * (aMass / total);
        if (a.free) {
          a.anchorX = a.x;
          a.anchorY = curveY(a.x);
        }
        if (b.free) {
          b.anchorX = b.x;
          b.anchorY = curveY(b.x);
        }
        keepOffCurve(a);
        keepOffCurve(b);
        clampItem(a);
        clampItem(b);
      }
    }
    if (!moved) break;
  }

  return draft.map(({ preferSide: _preferSide, free: _free, ...marker }) => marker);
}

const BRAND_BUBBLE_WINDOW_MINUTES = 5;

/** Keep the first timed hit per brand inside each N-minute window. */
function dedupeTimedEventsByBrandWindow(
  events: LinkedInLivestreamBrandsData["events"],
  windowMinutes: number = BRAND_BUBBLE_WINDOW_MINUTES,
): LinkedInLivestreamBrandsData["events"] {
  const timed = events
    .filter((event) => event.bucket !== "physical")
    .sort(
      (a, b) =>
        a.minute - b.minute ||
        a.brandName.localeCompare(b.brandName) ||
        a.id.localeCompare(b.id),
    );
  const kept: LinkedInLivestreamBrandsData["events"] = [];
  const lastMinuteByBrand = new Map<string, number>();
  for (const event of timed) {
    const key = eventBrandKey(event);
    if (!key) continue;
    const previous = lastMinuteByBrand.get(key);
    if (previous != null && event.minute - previous < windowMinutes) continue;
    lastMinuteByBrand.set(key, event.minute);
    kept.push(event);
  }
  return kept;
}

/** Timed appearances (5-min brand window) plus one physical marker per brand. */
function allLogoEvents(
  events: LinkedInLivestreamBrandsData["events"],
): LinkedInLivestreamBrandsData["events"] {
  const timed = dedupeTimedEventsByBrandWindow(events);
  const physical: LinkedInLivestreamBrandsData["events"] = [];
  const seen = new Set<string>();
  for (const event of events) {
    if (event.bucket !== "physical") continue;
    const key = eventBrandKey(event);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    physical.push(event);
  }
  return [...timed, ...physical];
}

const SvgTypeMark: React.FC<{
  bucket: LivestreamPresenceBucket;
  cx: number;
  cy: number;
}> = ({ bucket, cx, cy }) => {
  const color = PLACEMENT_BUCKET_COLORS[bucket];
  const halo = "rgba(13,13,18,0.92)";
  if (bucket === "content") {
    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill={halo} />
        <circle cx={cx} cy={cy} r={5.6} fill={color} />
      </g>
    );
  }
  if (bucket === "physical") {
    return (
      <g>
        <rect x={cx - 7} y={cy - 7} width={14} height={14} rx={1.6} fill={halo} />
        <rect x={cx - 5} y={cy - 5} width={10} height={10} rx={1.2} fill={color} />
      </g>
    );
  }
  if (bucket === "banner") {
    return (
      <g>
        <rect x={cx - 9} y={cy - 5} width={18} height={10} rx={1.6} fill={halo} />
        <rect x={cx - 7} y={cy - 3.2} width={14} height={6.4} rx={1} fill={color} />
      </g>
    );
  }
  return (
    <g>
      <path d={`M${cx} ${cy - 8.2} L${cx + 8} ${cy + 6.4} L${cx - 8} ${cy + 6.4} Z`} fill={halo} />
      <path d={`M${cx} ${cy - 5.6} L${cx + 5.6} ${cy + 4.8} L${cx - 5.6} ${cy + 4.8} Z`} fill={color} />
    </g>
  );
};

const PresenceChart: React.FC<{
  stream: LinkedInLivestreamBrandsData["stream"];
  events: LinkedInLivestreamBrandsData["events"];
}> = ({ stream, events }) => {
  const frameRef = React.useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = React.useState(0);
  React.useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const measure = () => setFrameWidth(node.clientWidth || 0);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const width = 928;
  const height = 400;
  const logoSize = 28;
  const pad = { l: 62, r: 52, t: 52, b: 28 };
  const maxMinute = Math.max(1, ...stream.minutes.map((point) => point.minute), ...events.map((event) => event.minute));
  const maxViewers = Math.max(
    1,
    ...stream.minutes.map((point) => point.viewers),
    stream.peakViewers ?? 0,
  );
  const smoothedChat = contiguousSegments(stream.minutes).flatMap(smoothedPoints);
  const maxChat = Math.max(0, ...smoothedChat.map((point) => point.chat ?? 0));
  const yMax = niceCeil(maxViewers);
  const chatMax = maxChat > 0 ? 200 : 0;
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const xFor = (minute: number) => pad.l + (minute / maxMinute) * plotW;
  const yFor = (viewers: number) => pad.t + (1 - viewers / yMax) * plotH;
  const baselineY = pad.t + plotH;
  const yChat = (chat: number) => pad.t + (1 - (chatMax > 0 ? chat / chatMax : 0)) * plotH;
  const start = stream.startedAt ? new Date(stream.startedAt) : null;
  const axisClock = (minute: number) =>
    start && !Number.isNaN(start.getTime())
      ? formatHhMm(new Date(start.getTime() + minute * 60_000))
      : formatHhMm(new Date(minute * 60_000));
  const startMinute = start
    ? Number(
        new Intl.DateTimeFormat("en-GB", {
          timeZone: "America/Santiago",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(start)
          .find((part) => part.type === "minute")?.value ?? "0",
      )
    : 0;
  const firstTick =
    startMinute === 0 || startMinute === 30 ? 0 : startMinute < 30 ? 30 - startMinute : 60 - startMinute;
  const xTicks: number[] = [];
  for (let minute = firstTick; minute <= maxMinute; minute += 30) xTicks.push(minute);

  const stroke =
    stream.channelColor && /^#[0-9a-fA-F]{6}$/.test(stream.channelColor)
      ? stream.channelColor
      : "#7056EB";
  const scale = frameWidth > 0 ? frameWidth / width : 1;
  const logoPx = logoSize * scale;
  const series = curveSeries(stream.minutes, stream.peakViewers ?? 0);
  const yAtMinute = (minute: number) => yFor(viewersOnCurve(series, minute));
  const minuteFromX = (x: number) =>
    clamp(((x - pad.l) / Math.max(1, plotW)) * maxMinute, 0, maxMinute);
  const logoEvents = allLogoEvents(events);
  const curveMarks = dedupeTimedEventsByBrandWindow(events);
  const markerBounds = {
    minX: pad.l + logoSize / 2,
    maxX: width - pad.r - logoSize / 2,
    minY: pad.t + logoSize / 2,
    maxY: baselineY - logoSize / 2,
  };
  const curveYAtX = (x: number) => yAtMinute(minuteFromX(x));
  const markers = layoutSpreadBrandMarkers(
    logoEvents,
    xFor,
    yAtMinute,
    curveYAtX,
    logoSize,
    markerBounds,
  );
  const stemFeet = spreadStemFeet(markers, curveYAtX, {
    minX: pad.l,
    maxX: width - pad.r,
  });

  return (
    <Box ref={frameRef} sx={{ position: "relative", overflow: "visible" }}>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Espectadores, chat y marcas minuto a minuto"
        style={{ display: "block", overflow: "visible" }}
      >
        <text
          x={0}
          y={16}
          textAnchor="start"
          fill={NAVY}
          fontFamily="DM Sans, sans-serif"
          fontSize={12}
          fontWeight={700}
        >
          Espectadores
        </text>
        {chatMax > 0 ? (
          <text
            x={width - 8}
            y={16}
            textAnchor="end"
            fill="rgba(255, 255, 255, 0.88)"
            fontFamily="DM Sans, sans-serif"
            fontSize={12}
            fontWeight={700}
          >
            Mensajes chat / min
          </text>
        ) : null}
        {[0, yMax / 2, yMax].map((tick, index) => {
          const y = yFor(tick);
          const chatTick = chatMax > 0 ? [0, chatMax / 2, chatMax][index] ?? 0 : null;
          return (
            <g key={tick}>
              <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke={CARD_BORDER} strokeWidth={1} />
              <text
                x={pad.l - 10}
                y={y + 4}
                textAnchor="end"
                fill={NAVY}
                fontFamily="DM Sans, sans-serif"
                fontSize={14}
                fontWeight={700}
              >
                {formatCount(tick)}
              </text>
              {chatTick != null ? (
                <text
                  x={width - pad.r + 10}
                  y={y + 4}
                  textAnchor="start"
                  fill="rgba(255, 255, 255, 0.88)"
                  fontFamily="DM Sans, sans-serif"
                  fontSize={14}
                  fontWeight={700}
                >
                  {formatCount(chatTick)}
                </text>
              ) : null}
            </g>
          );
        })}
        {xTicks.map((minute) => (
          <text
            key={minute}
            x={xFor(minute)}
            y={height - 8}
            textAnchor="middle"
            fill={MUTED}
            fontFamily="DM Sans, sans-serif"
            fontSize={14}
            fontWeight={600}
          >
            {axisClock(minute)}
          </text>
        ))}
        {chatMax > 0 ? (
          <>
            <path
              d={smoothAreaPath(stream.minutes, xFor, (point) => yChat(point.chat ?? 0), baselineY)}
              fill={CHAT_FILL}
            />
            <path
              d={mappedSegments(stream.minutes, xFor, (point) => yChat(point.chat ?? 0))
                .map(curvePath)
                .join(" ")}
              fill="none"
              stroke={CHAT_STROKE}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : null}
        {markers.map((marker) => {
          const foot = stemFeet.get(marker.key) ?? {
            x: marker.anchorX,
            y: marker.anchorY,
          };
          const dx = marker.x - foot.x;
          const dy = marker.y - foot.y;
          const dist = Math.hypot(dx, dy);
          const radius = logoSize / 2 + 1;
          const mark = 7;
          if (dist <= radius + mark) return null;
          const start = mark / dist;
          const end = (dist - radius) / dist;
          const x1 = foot.x + dx * start;
          const y1 = foot.y + dy * start;
          const x2 = foot.x + dx * end;
          const y2 = foot.y + dy * end;
          // Bend the stem slightly so nearby lines fan instead of stacking.
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const nx = dist > 0 ? -dy / dist : 0;
          const ny = dist > 0 ? dx / dist : 0;
          const bend = clamp((marker.x - foot.x) * 0.18, -18, 18);
          const cpx = midX + nx * bend;
          const cpy = midY + ny * bend;
          return (
            <path
              key={`stem-${marker.key}`}
              d={`M${x1.toFixed(1)} ${y1.toFixed(1)} Q${cpx.toFixed(1)} ${cpy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`}
              fill="none"
              stroke={PLACEMENT_BUCKET_COLORS[marker.bucket]}
              strokeWidth={1.6}
              opacity={0.7}
              strokeLinecap="round"
            />
          );
        })}
        <path
          d={smoothPath(stream.minutes, xFor, yFor, stream.peakViewers ?? 0)}
          fill="none"
          stroke={stroke}
          strokeWidth={4.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {curveMarks.map((event) => (
          <SvgTypeMark
            key={`type-${event.id || `${eventBrandKey(event)}:${event.bucket}:${event.minute}`}`}
            bucket={event.bucket}
            cx={xFor(event.minute)}
            cy={yAtMinute(event.minute)}
          />
        ))}
        {markers
          .filter((marker) => marker.bucket === "physical")
          .map((marker) => {
            const foot = stemFeet.get(marker.key) ?? {
              x: marker.anchorX,
              y: marker.anchorY,
            };
            return (
              <SvgTypeMark
                key={`type-physical-${marker.key}`}
                bucket="physical"
                cx={foot.x}
                cy={foot.y}
              />
            );
          })}
      </svg>
      {markers.map((marker) => (
        <Box
          key={marker.key}
          sx={{
            position: "absolute",
            left: `${(marker.x / width) * 100}%`,
            top: `${(marker.y / height) * 100}%`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            filter:
              "drop-shadow(0 1px 1px rgba(0,0,0,0.55)) drop-shadow(0 3px 6px rgba(0,0,0,0.42))",
          }}
        >
          <BrandMark
            name={displayBrandName(marker.name)}
            s3Key={marker.s3Key}
            logoUrl={marker.logoUrl}
            size={logoPx}
          />
        </Box>
      ))}
    </Box>
  );
};

export type LivestreamBrandsAssetBrandMetrics = {
  brandId: string | null;
  brandName: string;
  appearanceTypes: Array<{ key: LivestreamPresenceBucket; label: string }>;
  timedAppearanceCount: number;
  placementCount: number;
  avgViewersAtAppearances: number | null;
  chatPerMinuteAtAppearances: number | null;
  events: Array<{
    id: string;
    minute: number;
    bucket: LivestreamPresenceBucket;
    bucketLabel: string;
    mentionType: "paid" | "organic" | "neutral" | null;
    viewersAtMinute: number;
    chatPerMinuteAtMinute: number;
  }>;
};

export type LivestreamBrandsAssetMetrics = {
  stream: {
    id: string;
    programName: string;
    title: string;
    channelName: string;
    broadcastDay: string;
    startedAt: string | null;
    avgViewers: number | null;
    peakViewers: number | null;
    chatMessages: number;
  };
  episodeAppearances: number;
  shownInTable: LivestreamBrandsAssetBrandMetrics[];
  hiddenFromTable: LivestreamBrandsAssetBrandMetrics[];
  physicalPlacements: LivestreamBrandsAssetBrandMetrics[];
};

type BrandTableRow = {
  brand: LinkedInLivestreamBrandsData["brands"][number];
  types: Array<{ key: LivestreamPresenceBucket; label: string }>;
  count: number;
  placementCount: number;
  audience: { avgViewers: number | null; chatPerMinute: number | null };
  timedEvents: LinkedInLivestreamBrandsData["events"];
  brandEvents: LinkedInLivestreamBrandsData["events"];
};

function buildBrandTableRows(data: LinkedInLivestreamBrandsData): {
  events: LinkedInLivestreamBrandsData["events"];
  episode: { avgViewers: number | null; chat: number };
  tableRows: BrandTableRow[];
  placementRows: BrandTableRow[];
  mentionRows: BrandTableRow[];
  visibleMentionRows: BrandTableRow[];
  hiddenMentionRows: BrandTableRow[];
  episodeAppearances: number;
} {
  const maxMinute = Math.max(
    1,
    ...data.stream.minutes.map((point) => point.minute),
    ...data.events.map((event) => event.minute),
  );
  const events = asCreditsWindow(data.events, maxMinute);
  const episode = episodeConsolidado(data.stream);
  const tableRows = data.brands
    .map((brand) => {
      const brandEvents = events.filter((event) =>
        brand.brandId
          ? event.brandId === brand.brandId
          : event.brandName === brand.brandName,
      );
      const timedEvents = brandEvents.filter((event) => event.bucket !== "physical");
      const placementCount = brandEvents.length - timedEvents.length;
      const types = PRESENCE_TYPES.filter(
        (type) => type.key !== "physical" && timedEvents.some((event) => event.bucket === type.key),
      );
      return {
        brand,
        types,
        count: timedEvents.length,
        placementCount,
        audience: appearanceAudience(
          timedEvents.map((event) => event.minute),
          data.stream.minutes,
        ),
        timedEvents,
        brandEvents,
      };
    })
    .filter((row) => row.count > 0 || row.placementCount > 0);
  const placementRows = tableRows
    .filter((row) => row.placementCount > 0)
    .sort((a, b) => a.brand.brandName.localeCompare(b.brand.brandName));
  const mentionRows = tableRows
    .filter((row) => row.count > 0)
    .sort(
      (a, b) =>
        (b.audience.avgViewers ?? 0) - (a.audience.avgViewers ?? 0) ||
        b.count - a.count ||
        a.brand.brandName.localeCompare(b.brand.brandName),
    );
  const visibleMentionRows = mentionRows.slice(0, LIVESTREAM_BRANDS_MAX_TABLE_ROWS);
  const hiddenMentionRows = mentionRows.slice(LIVESTREAM_BRANDS_MAX_TABLE_ROWS);
  const episodeAppearances = tableRows.reduce((sum, row) => sum + row.count + row.placementCount, 0);
  return {
    events,
    episode,
    tableRows,
    placementRows,
    mentionRows,
    visibleMentionRows,
    hiddenMentionRows,
    episodeAppearances,
  };
}

function toBrandMetrics(
  row: BrandTableRow,
  minutes: LinkedInLivestreamBrandsData["stream"]["minutes"],
): LivestreamBrandsAssetBrandMetrics {
  return {
    brandId: row.brand.brandId,
    brandName: displayBrandName(row.brand.brandName),
    appearanceTypes: row.types,
    timedAppearanceCount: row.count,
    placementCount: row.placementCount,
    avgViewersAtAppearances: row.audience.avgViewers,
    chatPerMinuteAtAppearances: row.audience.chatPerMinute,
    events: row.brandEvents
      .slice()
      .sort(
        (a, b) =>
          a.minute - b.minute ||
          a.bucket.localeCompare(b.bucket) ||
          a.id.localeCompare(b.id),
      )
      .map((event) => ({
        id: event.id,
        minute: event.minute,
        bucket: event.bucket,
        bucketLabel: PRESENCE_LABEL[event.bucket],
        mentionType: event.mentionType ?? null,
        viewersAtMinute: viewersAtMinute(minutes, event.minute),
        chatPerMinuteAtMinute: chatPerMinuteOnLine(minutes, event.minute),
      })),
  };
}

/** Structured metrics matching what the marcas-vivo asset shows vs hides. */
export function buildLivestreamBrandsAssetMetrics(
  data: LinkedInLivestreamBrandsData,
): LivestreamBrandsAssetMetrics {
  const {
    episode,
    visibleMentionRows,
    hiddenMentionRows,
    placementRows,
    episodeAppearances,
  } = buildBrandTableRows(data);
  const program =
    data.stream.programName?.trim() || data.stream.title.trim() || "Livestream";
  return {
    stream: {
      id: data.stream.id,
      programName: program,
      title: data.stream.title,
      channelName: data.stream.channelName,
      broadcastDay: formatBroadcastDay(data.stream.startedAt),
      startedAt: data.stream.startedAt,
      avgViewers: episode.avgViewers,
      peakViewers:
        data.stream.peakViewers && data.stream.peakViewers > 0
          ? Math.round(data.stream.peakViewers)
          : null,
      chatMessages: episode.chat,
    },
    episodeAppearances,
    shownInTable: visibleMentionRows.map((row) =>
      toBrandMetrics(row, data.stream.minutes),
    ),
    hiddenFromTable: hiddenMentionRows.map((row) =>
      toBrandMetrics(row, data.stream.minutes),
    ),
    physicalPlacements: placementRows.map((row) =>
      toBrandMetrics(row, data.stream.minutes),
    ),
  };
}

function formatBrandMetricsBlock(
  brand: LivestreamBrandsAssetBrandMetrics,
  index: number,
): string {
  const types =
    brand.appearanceTypes.map((type) => type.label).join(", ") ||
    (brand.placementCount > 0 ? "Placement físico" : "—");
  const lines = [
    `${index}. ${brand.brandName}`,
    `   Tipos en asset: ${types}`,
    `   Apariciones timed (mención/banner/créditos): ${brand.timedAppearanceCount}`,
    `   Placements físicos: ${brand.placementCount}`,
    `   Audiencia promedio en apariciones timed: ${
      brand.avgViewersAtAppearances != null
        ? formatCount(brand.avgViewersAtAppearances)
        : "—"
    }`,
    `   Chat/min promedio en apariciones timed: ${formatChatRate(brand.chatPerMinuteAtAppearances)}`,
    "   Eventos individuales:",
  ];
  if (!brand.events.length) {
    lines.push("     (ninguno)");
    return lines.join("\n");
  }
  for (const event of brand.events) {
    const mentionType = event.mentionType ? ` · ${event.mentionType}` : "";
    lines.push(
      `     - min ${event.minute} · ${event.bucketLabel} (${event.bucket})${mentionType} · viewers ${formatCount(event.viewersAtMinute)} · chat/min ${formatChatRate(event.chatPerMinuteAtMinute)}${event.id ? ` · id ${event.id}` : ""}`,
    );
  }
  return lines.join("\n");
}

/** Plain-text dump for pasting into Claude to write LinkedIn post copy. */
export function buildLivestreamBrandsRawMetricsText(
  data: LinkedInLivestreamBrandsData,
): string {
  const metrics = buildLivestreamBrandsAssetMetrics(data);
  const { stream } = metrics;
  const sections = [
    "Raw metrics for LinkedIn asset: marcas en vivo",
    "Use these numbers to write LinkedIn post copy. Prefer shown brands; hidden brands were cut from the table for space.",
    "",
    "## Episodio",
    `Programa: ${stream.programName}`,
    `Título: ${stream.title}`,
    `Canal: ${stream.channelName}`,
    `Día: ${stream.broadcastDay || "—"}`,
    `Started at: ${stream.startedAt || "—"}`,
    `Stream id: ${stream.id}`,
    `Audiencia promedio episodio: ${stream.avgViewers != null ? formatCount(stream.avgViewers) : "—"}`,
    `Peak viewers: ${stream.peakViewers != null ? formatCount(stream.peakViewers) : "—"}`,
    `Mensajes de chat (total): ${stream.chatMessages > 0 ? formatCount(stream.chatMessages) : "—"}`,
    `Apariciones de marca (total episodio, incl. placements): ${metrics.episodeAppearances}`,
    "",
    `## Mostradas en el asset (top ${LIVESTREAM_BRANDS_MAX_TABLE_ROWS} por audiencia en vivo)`,
  ];
  if (!metrics.shownInTable.length) {
    sections.push("(ninguna)");
  } else {
    metrics.shownInTable.forEach((brand, index) => {
      sections.push(formatBrandMetricsBlock(brand, index + 1));
    });
  }
  sections.push("");
  sections.push(
    `## Ocultas del asset (+${metrics.hiddenFromTable.length} marca${metrics.hiddenFromTable.length === 1 ? "" : "s"} más)`,
  );
  if (!metrics.hiddenFromTable.length) {
    sections.push("(ninguna)");
  } else {
    metrics.hiddenFromTable.forEach((brand, index) => {
      sections.push(formatBrandMetricsBlock(brand, index + 1));
    });
  }
  sections.push("");
  sections.push("## Placements físicos (fila episodio del asset)");
  if (!metrics.physicalPlacements.length) {
    sections.push("(ninguno)");
  } else {
    metrics.physicalPlacements.forEach((brand, index) => {
      sections.push(formatBrandMetricsBlock(brand, index + 1));
    });
  }
  sections.push("");
  sections.push("## JSON");
  sections.push(JSON.stringify(metrics, null, 2));
  return sections.join("\n");
}

const LinkedInLivestreamBrandsAsset: React.FC<{
  data: LinkedInLivestreamBrandsData;
}> = ({ data }) => {
  const program = data.stream.programName?.trim() || data.stream.title.trim() || "Livestream";
  const day = formatBroadcastDay(data.stream.startedAt);
  const {
    events,
    episode,
    placementRows,
    visibleMentionRows,
    hiddenMentionRows,
    episodeAppearances,
  } = buildBrandTableRows(data);
  const visibleTypes = PRESENCE_TYPES.filter((type) =>
    events.some((event) => event.bucket === type.key),
  );
  const hiddenBrandCount = hiddenMentionRows.length;
  /** Match title (16×1.1) + gap + subtitle (13) so avatars align with the text stack. */
  const TABLE_AVATAR_PX = 34;
  const tableColumns = `${TABLE_AVATAR_PX}px minmax(0, 1.4fr) 176px 158px 82px`;
  return (
    <LinkedInAssetCanvas>
      <Box>
        <AssetKicker>
          {[day, data.stream.channelName, "YouTube en vivo"].filter(Boolean).join(" · ")}
        </AssetKicker>
        <AssetTitle>
          {program}
          <br />
          ¿Qué marcas aparecieron en vivo?
        </AssetTitle>
      </Box>

      <Card sx={{ pt: "24px", pb: "12px" }}>
        <CardLabel sx={{ mb: "10px" }}>Espectadores, chat y menciones</CardLabel>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
          <ChannelAvatar
            src={channelImageSrc(data.stream)}
            accentColor={data.stream.channelColor || "#7056EB"}
            size={TABLE_AVATAR_PX}
          />
          <Typography
            sx={{ m: 0, fontFamily: SANS, fontWeight: 700, fontSize: 16, lineHeight: 1.1, color: NAVY }}
          >
            {program}
          </Typography>
        </Box>
        <PresenceChart stream={data.stream} events={events} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "16px",
            mt: "14px",
            pt: "12px",
            borderTop: `1px solid ${CARD_BORDER}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              aria-hidden
              sx={{
                width: 16,
                height: 3,
                borderRadius: "2px",
                bgcolor:
                  data.stream.channelColor && /^#[0-9a-fA-F]{6}$/.test(data.stream.channelColor)
                    ? data.stream.channelColor
                    : "#7056EB",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ m: 0, fontFamily: SANS, fontWeight: 600, fontSize: 13, color: MUTED }}>
              Espectadores
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              aria-hidden
              sx={{
                width: 14,
                height: 8,
                borderRadius: "1px",
                bgcolor: CHAT_FILL,
                border: `1.5px solid ${CHAT_STROKE}`,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ m: 0, fontFamily: SANS, fontWeight: 600, fontSize: 13, color: MUTED }}>
              Mensajes chat / min
            </Typography>
          </Box>
          {visibleTypes.map((type) => (
            <Box key={type.key} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <MarkFigure bucket={type.key} />
              <Typography sx={{ m: 0, fontFamily: SANS, fontWeight: 600, fontSize: 13, color: MUTED }}>
                {type.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Card sx={{ pb: "12px" }}>
        <CardLabel sx={{ mb: "12px" }}>Presencia de marcas consolidada</CardLabel>
        <DataTable
          columns={tableColumns}
          nameFontSize={16}
          metaFontSize={13}
          rowPy="4px"
          avatarSize={TABLE_AVATAR_PX}
          headers={[
            { label: "Episodio" },
            {
              label: (
                <>
                  Audiencia en vivo
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", mt: "2px" }}
                  >
                    promedio
                  </Box>
                </>
              ),
            },
            {
              label: (
                <>
                  Mensajes de chat
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", mt: "2px" }}
                  >
                    total
                  </Box>
                </>
              ),
            },
            {
              label: (
                <>
                  Apariciones
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", whiteSpace: "nowrap", mt: "2px" }}
                  >
                    de marca
                  </Box>
                </>
              ),
            },
          ]}
          rows={[
            {
              key: "episode",
              avatar: (
                <ChannelAvatar
                  src={channelImageSrc(data.stream)}
                  accentColor={data.stream.channelColor || "#7056EB"}
                  size={TABLE_AVATAR_PX}
                  sx={{ border: "none" }}
                />
              ),
              name: data.stream.programName?.trim() || "Todo el episodio",
              meta: (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <Box component="span">Placements físicos</Box>
                  {placementRows.length > 0 ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                      <MarkFigure bucket="physical" />
                      {placementRows.map(({ brand }) => (
                        <Box
                          key={brandTypeKey(brand)}
                          component="span"
                          sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
                        >
                          <BrandMark
                            name={displayBrandName(brand.brandName)}
                            s3Key={brand.s3Key}
                            logoUrl={brand.logoUrl}
                            size={14}
                          />
                          {displayBrandName(brand.brandName)}
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ),
              cells: [
                episode.avgViewers != null ? formatCount(episode.avgViewers) : "—",
                episode.chat > 0 ? formatCount(episode.chat) : "—",
                episodeAppearances || "—",
              ],
            },
          ]}
        />
        <Box
          aria-hidden
          sx={{
            height: 0,
            my: "18px",
            borderTop: `1px solid ${CARD_BORDER}`,
          }}
        />
        <DataTable
          columns={tableColumns}
          nameFontSize={16}
          metaFontSize={13}
          rowPy="8px"
          headerAlign="start"
          headers={[
            { label: "Marca" },
            {
              label: (
                <>
                  Audiencia en vivo
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", whiteSpace: "nowrap", mt: "2px" }}
                  >
                    promedio
                  </Box>
                </>
              ),
            },
            {
              label: (
                <>
                  Engagement en vivo
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", mt: "2px" }}
                  >
                    mensajes chat / min
                  </Box>
                </>
              ),
            },
            {
              label: (
                <>
                  Apariciones
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 500, fontSize: 12, letterSpacing: 0, textTransform: "none", whiteSpace: "nowrap", mt: "2px" }}
                  >
                    de marca
                  </Box>
                </>
              ),
            },
          ]}
          rows={[
            ...visibleMentionRows.map(({ brand, types, count, audience }) => ({
              key: brandTypeKey(brand),
              avatar: (
                <BrandMark
                  name={displayBrandName(brand.brandName)}
                  s3Key={brand.s3Key}
                  logoUrl={brand.logoUrl}
                  size={TABLE_AVATAR_PX}
                />
              ),
              name: displayBrandName(brand.brandName),
              meta: (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {types.map((type) => (
                    <Box key={type.key} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <MarkFigure bucket={type.key} />
                      {type.label}
                    </Box>
                  ))}
                </Box>
              ),
              cells: [
                audience.avgViewers != null ? formatCount(audience.avgViewers) : "—",
                formatChatRate(audience.chatPerMinute),
                count,
              ],
            })),
            ...(hiddenBrandCount > 0
              ? [
                  {
                    key: "more-brands",
                    name: (
                      <Box component="span" sx={{ color: MUTED, fontWeight: 600 }}>
                        {`+${hiddenBrandCount} marca${hiddenBrandCount === 1 ? "" : "s"} más`}
                      </Box>
                    ),
                    cells: ["", "", ""],
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <MethodologyFooter compact>
        {`: Audiencia en vivo: por minuto y promedio del episodio; durante apariciones, el promedio en el minuto en donde apareció la marca. Mensajes de chat: total del en vivo. Mensajes chat / min: promedio de mensajes por minuto en cada aparición. Placements físicos: durante todo el episodio.`}
      </MethodologyFooter>
    </LinkedInAssetCanvas>
  );
};

export default LinkedInLivestreamBrandsAsset;
